import type { MaybeRef } from 'vue';
import { computed, onUnmounted, ref, toValue, watchSyncEffect } from 'vue';

import type { Plaid } from '../../../types/plaid';
import useScriptTag from '../useScriptTag/useScriptTag';

const PLAID_SDK_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

export default function usePlaidLink(options: MaybeRef<Plaid.CreateConfig>) {
  const { isLoaded: isPlaidScriptLoaded, error } = useScriptTag(PLAID_SDK_URL);

  const plaid = ref<Plaid.LinkHandler | null>(null);
  const iframeLoaded = ref(false);

  // We want to trigger this as soon as options ref changes
  watchSyncEffect(() => {
    const optionsVal = toValue(options);

    if (!isPlaidScriptLoaded.value) {
      return;
    }

    if (!optionsVal.token && !optionsVal.receivedRedirectUri) {
      return;
    }

    if (!window.Plaid) {
      throw new Error('Error loading Plaid SDK');
    }

    // destroy & exit existing instances so we don't
    // have multiple Plaid link modals at the same time
    teardown();

    plaid.value = window.Plaid.create({
      ...optionsVal,
      onLoad: () => {
        iframeLoaded.value = true;
        optionsVal.onLoad?.();
      },
    });
  });

  const ready = computed(
    () => !error.value && plaid.value !== null && (isPlaidScriptLoaded.value || iframeLoaded.value),
  );

  function teardown() {
    if (plaid.value) {
      plaid.value.exit({ force: true });
      plaid.value.destroy();

      iframeLoaded.value = false;
    }
  }

  onUnmounted(teardown);

  return {
    ready,
    error,
    open: () => plaid.value?.open(),
    exit: (options?: Plaid.ExitOptions) => plaid.value?.exit(options),
  };
}
